import React, { useState, useEffect, useRef, useCallback } from 'react';
import CustomerInfoOverlay from './CustomerInfoOverlay';
import {
  ArrowLeft, ChevronRight, CheckCircle2, XCircle, AlertTriangle, PauseCircle, Wifi, WifiOff, Ban, Tv, Phone, MessageCircle,  Wallet, RefreshCw, Package, MessageSquare, Edit2, History, FileText, KeyRound, Link2, User, CreditCard, Settings2, Activity,
  Cpu, ShoppingCart, ExternalLink, PackagePlus, Power, RotateCcw, Trash2, Clock, StickyNote, Eye, Radio, Info, Unplug, Router,
} from 'lucide-react';
import { format, differenceInCalendarDays } from 'date-fns';
import '../../styles/customer-mobile.css';

// Mobile (App View) customer workspace. Presentation and navigation only: every value, panel and
// handler comes from CustomerDetailPage, so no business rule lives here.
//
//   Header   who the customer is (always visible)
//   Tabs     Overview · Services · Payments · Complaints · More
//   Screens  anything with a form (renew, collect, edit …) opens full-screen with a Back button

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const fmtDate = d => (d ? format(new Date(d), 'dd MMM yyyy') : '—');

// Validity of one service, in words (never colour alone).
export function validity(expiry) {
  if (!expiry) return { tone: 'mute', label: 'No expiry date', days: null };
  const days = differenceInCalendarDays(new Date(expiry), new Date());
  if (days < 0) return { tone: 'bad', label: `Expired ${-days} day${days === -1 ? '' : 's'} ago`, days };
  if (days === 0) return { tone: 'warn', label: 'Expires today', days };
  if (days <= 5) return { tone: 'warn', label: `${days} day${days === 1 ? '' : 's'} left`, days };
  return { tone: 'ok', label: `${days} days left`, days };
}

const TONE_ICON = { ok: CheckCircle2, bad: XCircle, warn: AlertTriangle, info: CheckCircle2, mute: PauseCircle };

export function Chip({ tone = 'mute', icon, children, title }) {
  const Icon = icon || TONE_ICON[tone];
  return <span className={`cm-chip ${tone}`} title={title}><Icon size={14} aria-hidden="true" />{children}</span>;
}

function Row({ icon: Icon, title, sub, onClick, href, danger, disabled, trailing }) {
  const cls = `cm-row${danger ? ' danger' : ''}`;
  const inner = (
    <>
      {Icon && <span className="ico" aria-hidden="true"><Icon size={18} /></span>}
      <span className="txt"><span className="t">{title}</span>{sub && <span className="s">{sub}</span>}</span>
      {trailing}
      {(onClick || href) && !disabled && <ChevronRight size={18} aria-hidden="true" color="var(--cm-muted)" />}
    </>
  );
  if (href) return <a className={cls} href={href}>{inner}</a>;
  return <button type="button" className={cls} onClick={onClick} disabled={disabled}>{inner}</button>;
}

function Disclosure({ icon: Icon, title, sub, children, open }) {
  return (
    <details className="cm-block cm-details" open={open || undefined}>
      <summary>
        {Icon && <span className="cm-row" style={{ width: 'auto', minHeight: 0, padding: 0, border: 0 }}><span className="ico" aria-hidden="true"><Icon size={18} /></span></span>}
        <span className="txt" style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block' }}>{title}</span>
          {sub && <span style={{ display: 'block', fontSize: '.875rem', color: 'var(--cm-muted)', fontWeight: 500, overflowWrap: 'anywhere' }}>{sub}</span>}
        </span>
        <ChevronRight className="chev" size={18} aria-hidden="true" color="var(--cm-muted)" />
      </summary>
      <div className="body">{children}</div>
    </details>
  );
}

function Tabs({ tabs, value, onChange }) {
  const refs = useRef({});
  const onKey = (e, i) => {
    let n = null;
    if (e.key === 'ArrowRight') n = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = tabs.length - 1;
    if (n == null) return;
    e.preventDefault();
    onChange(tabs[n].key);
    refs.current[tabs[n].key]?.focus();
  };
  return (
    <div className="cm-tabs" role="tablist" aria-label="Customer sections">
      {tabs.map(({ key, label, icon: Icon, count }, i) => (
        <button key={key} ref={el => { refs.current[key] = el; }} type="button" role="tab" id={`cm-tab-${key}`} className="cm-tab"
          aria-selected={value === key} aria-controls={`cm-panel-${key}`} tabIndex={value === key ? 0 : -1}
          onClick={() => onChange(key)} onKeyDown={e => onKey(e, i)}>
          <Icon size={20} aria-hidden="true" />
          {label}
          {count > 0 && <span className="cm-count" aria-label={`${count} open`}>{count}</span>}
        </button>
      ))}
    </div>
  );
}

// Full-screen task view with a Back button. The phone's own Back button closes it too.
function Screen({ title, onClose, children }) {
  const backRef = useRef(null);
  const closedByPop = useRef(false);
  useEffect(() => {
    const opener = document.activeElement;
    backRef.current?.focus();
    window.history.pushState({ ...(window.history.state || {}), cmScreen: true }, '');
    const onPop = () => { closedByPop.current = true; onClose(); };
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('popstate', onPop);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('popstate', onPop);
      document.removeEventListener('keydown', onKey);
      if (!closedByPop.current && window.history.state?.cmScreen) window.history.back();
      opener?.focus?.();
    };
    // eslint-disable-next-line
  }, []);
  return (
    <div className="cm-screen" role="dialog" aria-modal="true" aria-labelledby="cm-screen-title">
      <div className="cm-screen-bar">
        <button ref={backRef} type="button" className="cm-back" onClick={onClose}><ArrowLeft size={20} aria-hidden="true" />Back</button>
        <h2 id="cm-screen-title" className="cm-screen-title">{title}</h2>
      </div>
      <div className="cm-screen-body"><div className="cm-panel">{children}</div></div>
    </div>
  );
}

export function CustomerMobile({
  customer, balance, payments, complaints, isActive, hasCatvService, hasBroadbandService, hasBB, hasCatv, hideCollect, bbActive,
  allowed, imbs, pendingText, linked, isAdmin, panelFor, nodes, labels, onBack, goToCustomer,
  toggleCatv, toggleBb, catvBusy, bbBusy, retrack, retracking, kick, kicking, openOnu, gtplToggle, onDelete, initialTab, initialScreen,
}) {
  const can = k => allowed.has(k);
  const [tab, setTab] = useState(initialTab || 'overview');
  const [svc, setSvc] = useState(hasCatvService ? 'cabletv' : 'broadband');
  const [screen, setScreen] = useState(initialScreen || null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const closeScreen = useCallback(() => setScreen(null), []);
  const go = useCallback(t => { setTab(t); window.scrollTo?.({ top: 0 }); }, []);
  const openService = s => { setSvc(s); go('services'); };

  const catvOn = customer.status === 'active';
  const catvSolo = hasCatvService && !hasBroadbandService;
  const openComplaints = (complaints || []).filter(c => ['open', 'in_progress'].includes(c.status)).length;
  const a = customer.address || {};
  const house = customer.catv?.houseNumber || customer.broadband?.houseNumber;
  const address = [house, a.line1, a.line2, a.area, a.city, a.pincode].filter(Boolean).join(', ');
  const wa = String(customer.mobile || '').replace(/\D/g, '').slice(-10);
  const notes = (customer.notes || '').trim();
  const lastPay = (payments || [])[0];
  const catvPlan = customer.catv?.package?.displayName || customer.catv?.package?.name;
  const bbPlan = customer.broadband?.plan?.displayName;
  const catvV = validity(customer.catv?.expiryDate);
  const bbV = validity(customer.broadband?.expiryDate);
  const accountState = isActive ? { tone: 'ok', text: 'Active' } : customer.status === 'suspended' ? { tone: 'warn', text: 'Suspended', icon: PauseCircle } : { tone: 'bad', text: 'Inactive' };

  // The single most useful next step, derived from the real account state.
  const services = [
    hasCatvService && { name: 'Cable TV', on: catvOn, v: catvV },
    hasBroadbandService && { name: 'Broadband', on: bbActive, v: bbV },
  ].filter(Boolean);
  let next;
  if (balance > 0 && !hideCollect && can('collect')) next = { tone: 'bad', title: `₹${fmt(balance)} payment pending`, sub: 'Collect it now.', cta: 'Collect payment', screen: 'collect', icon: Wallet };
  else {
    const lapsed = services.find(s => s.on && s.v.days != null && s.v.days <= 5);
    if (lapsed && can('renew')) next = { tone: lapsed.v.days < 0 ? 'bad' : 'warn', title: `${lapsed.name}: ${lapsed.v.label.toLowerCase()}`, sub: `Valid until ${fmtDate(lapsed === services[0] && hasCatvService ? customer.catv?.expiryDate : customer.broadband?.expiryDate)}.`, cta: 'Renew', screen: 'renew', icon: RefreshCw };
    else if (services.some(s => !s.on)) next = { tone: 'warn', title: `${services.filter(s => !s.on).map(s => s.name).join(' and ')} connection is closed`, sub: 'Reactivate it from Services.', cta: 'Open Services', tab: 'services', icon: Settings2 };
    else next = { tone: 'ok', title: 'Everything is up to date', sub: 'No dues and nothing expiring in the next 5 days.' };
  }
  const NextIcon = next.icon;

  const tabs = [
    { key: 'overview', label: 'Overview', icon: Eye },
    { key: 'services', label: hasCatvService && hasBroadbandService ? 'Services' : hasBroadbandService ? 'Broadband' : 'Cable TV', icon: hasBroadbandService && !hasCatvService ? Wifi : Tv },
    { key: 'payments', label: 'Payments', icon: CreditCard },
    { key: 'complaints', label: 'Complaints', icon: MessageSquare, count: openComplaints },
    { key: 'more', label: 'More', icon: Settings2 },
  ];

  const onlineChip = imbs.linked && (
    imbs.loading ? <Chip tone="mute" icon={Clock}>Checking line…</Chip>
      : imbs.blocked ? <Chip tone="bad" icon={Ban} title={imbs.reason}>Blocked</Chip>
        : imbs.online ? <Chip tone="ok" icon={Wifi}>Online</Chip>
          : <Chip tone="mute" icon={WifiOff}>Offline</Chip>
  );

  const catvBlock = (
    <section aria-labelledby="cm-svc-cabletv">
      <div className="cm-block">
        <div className="cm-pad">
          <div className="cm-svc-top">
            <h2 id="cm-svc-cabletv" className="cm-svc-name">Cable TV</h2>
            <Chip tone={catvOn ? 'ok' : 'bad'}>{catvOn ? 'Active' : 'Inactive'}</Chip>
            {customer.catv?.gtplSuspended && <Chip tone="bad" icon={Ban}>Suspended on GTPL</Chip>}
          </div>
        </div>
        <dl className="cm-kv" style={{ paddingTop: 0 }}>
          <dt>Package</dt><dd>{catvPlan || '—'}</dd>
          <dt>Valid until</dt><dd>{fmtDate(customer.catv?.expiryDate)}<span className={`cm-svc-exp ${catvV.tone}`} style={{ display: 'block' }}>{catvV.label}</span></dd>
          <dt>Billing</dt><dd>{{ regular: 'Regular (monthly)', dtd: 'Date to date', amp: 'AMP' }[customer.catv?.catvType || customer.billing?.durationType || 'regular'] || 'Regular (monthly)'}</dd>
          {!catvOn && customer.catv?.closureReason && (<><dt>Closed because</dt><dd>{customer.catv.closureReason}</dd></>)}
        </dl>
      </div>
      <div className="cm-block">
        {can('subscription') && hasCatv && <Row icon={Package} title="Change package" sub="Package, add-on packages" onClick={() => setScreen('subscription')} />}
        {can('hardware') && <Row icon={Cpu} title="Edit box details" sub="Model, serial, entity, account number" onClick={() => setScreen('hardware')} />}
        <Row danger icon={catvOn ? Power : RefreshCw} title={catvOn ? 'Close Cable TV connection' : 'Reactivate Cable TV connection'}
          sub={catvOn ? 'Asks for a reason first' : 'Turns the connection back on'} disabled={catvBusy} onClick={toggleCatv} />
      </div>
      {!catvSolo && <Disclosure open icon={Cpu} title="Hardware details">{nodes.catvHw}</Disclosure>}
    </section>
  );

  const bbMonitor = hasBB && (
    <>
      {customer.broadband?.username
        ? <div className="cm-flat">{nodes.bbLive}</div>
        : <div className="cm-block cm-pad">No PPPoE username on this customer, so there is no live traffic to read.</div>}
      <div className="cm-flat">{nodes.bbHw}</div>
      <div className="cm-flat">{nodes.bbHist}</div>
    </>
  );
  const catvDetails = (
    <>
      <div className="cm-block">
        <dl className="cm-kv sm">
          <dt>Package</dt><dd>{catvPlan || '—'}</dd>
          <dt>Valid until</dt><dd>{fmtDate(customer.catv?.expiryDate)}<span className={`cm-svc-exp ${catvV.tone}`} style={{ display: 'block' }}>{catvV.label}</span></dd>
          <dt>Billing</dt><dd>{{ regular: 'Regular (monthly)', dtd: 'Date to date', amp: 'AMP' }[customer.catv?.catvType || customer.billing?.durationType || 'regular'] || 'Regular (monthly)'}</dd>
          {customer.catv?.gtplSuspended && (<><dt>GTPL</dt><dd>Suspended</dd></>)}
          {!catvOn && customer.catv?.closureReason && (<><dt>Closed because</dt><dd>{customer.catv.closureReason}</dd></>)}
        </dl>
      </div>
      <Disclosure open icon={Cpu} title="Current hardware">{nodes.catvHw}</Disclosure>
    </>
  );
  const bbPlanDl = (
    <dl className="cm-kv sm">
      <dt>Plan</dt><dd>{bbPlan || '—'}</dd>
      <dt>Valid until</dt><dd>{fmtDate(customer.broadband?.expiryDate)}<span className={`cm-svc-exp ${bbV.tone}`} style={{ display: 'block' }}>{bbV.label}</span></dd>
      {imbs.linked && imbs.packageName && (<><dt>Netzur package</dt><dd>{imbs.packageName}</dd></>)}
      {!bbActive && customer.broadband?.closureReason && (<><dt>Closed because</dt><dd>{customer.broadband.closureReason}</dd></>)}
    </dl>
  );
  const bbBlock = (
    <section aria-labelledby="cm-svc-broadband">
      <div className="cm-block">
        <div className="cm-pad">
          <div className="cm-svc-top">
            <h2 id="cm-svc-broadband" className="cm-svc-name">Broadband</h2>
            {hasCatvService && <Chip tone={bbActive ? 'ok' : 'bad'}>{bbActive ? 'Active' : 'Inactive'}</Chip>}
          </div>
        </div>
        {!hasBB && <dl className="cm-kv" style={{ paddingTop: 0 }}>
          <dt>Plan</dt><dd>{bbPlan || '—'}</dd>
          <dt>Valid until</dt><dd>{fmtDate(customer.broadband?.expiryDate)}<span className={`cm-svc-exp ${bbV.tone}`} style={{ display: 'block' }}>{bbV.label}</span></dd>
          {imbs.linked && imbs.packageName && (<><dt>Netzur package</dt><dd>{imbs.packageName}</dd></>)}
          {!bbActive && customer.broadband?.closureReason && (<><dt>Closed because</dt><dd>{customer.broadband.closureReason}</dd></>)}
        </dl>}
      </div>
      {hasBB && nodes.bbUsage && <Disclosure icon={Activity} title="Consumption & usage history" sub="Data used, today, week, month">{nodes.bbUsage}</Disclosure>}
      {<div className="cm-block">
        {can('subscription') && hasBB && <Row icon={Package} title="Change plan" sub="Plan and billing" onClick={() => setScreen('subscription')} />}
        {can('addons') && <Row icon={PackagePlus} title="Add-ons" sub="Extra plans and OTT packs" onClick={() => setScreen('addons')} />}
        {can('imbs') && <Row icon={ExternalLink} title="Netzur sync" sub="Subscriber, recharge, KYC" onClick={() => setScreen('imbs')} />}
        {can('hardware') && <Row icon={Cpu} title="Edit router details" sub="Router, serial, PON, username" onClick={() => setScreen('hardware')} />}
        {can('linked_catv') && <Row icon={Link2} title="Linked Cable TV" sub="Cable TV accounts on this plan" onClick={() => setScreen('linked_catv')} />}
        <Row danger icon={bbActive ? Power : RefreshCw} title={bbActive ? 'Close Broadband connection' : 'Reactivate Broadband connection'}
          sub={bbActive ? 'Asks for a reason first' : 'Turns the connection back on'} disabled={bbBusy} onClick={toggleBb} />
      </div>}
    </section>
  );

  const bothServices = hasCatvService && hasBroadbandService;

  return (
    <>
      <div className="cm" aria-hidden={screen ? 'true' : undefined} inert={screen ? '' : undefined}>
        <button type="button" className="cm-back" onClick={onBack}><ArrowLeft size={20} aria-hidden="true" />Customers</button>

        <header className="cm-hero">
          <div className="cm-hero-top">
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="cm-hero-namerow">
                <h1 className="cm-name">{customer.name}</h1>
                <button type="button" className="cm-info-btn" onClick={() => setInfoOpen(true)} aria-haspopup="dialog" aria-label="Customer information" title="Customer information"><Info size={16} aria-hidden="true" /></button>
                {hasCatvService && <button type="button" className="cm-retrack" disabled={retracking} onClick={retrack}><RotateCcw size={13} aria-hidden="true" />{retracking ? 'Retracking…' : 'Retrack'}</button>}
                {hasBroadbandService && !hasCatvService && customer.broadband?.username && <button type="button" className="cm-retrack" disabled={kicking} onClick={kick}><Unplug size={13} aria-hidden="true" />{kicking ? 'Working…' : 'Disconnect'}</button>}
              </div>
              <div className="cm-hero-chips" style={{ marginTop: 6 }}>
                <Chip tone={accountState.tone} icon={accountState.icon}>{accountState.text}</Chip>
                {hasBroadbandService && onlineChip}
              </div>
              <div className="cm-hero-sw">
                {hasCatvService && <button type="button" role="switch" aria-checked={!!catvOn} className={`cm-sw${catvOn ? ' on' : ''}`} disabled={catvBusy} onClick={toggleCatv}><span className="cm-sw-track" aria-hidden="true"><span /></span>Cable TV {catvOn ? 'Active' : 'Inactive'}</button>}
                {hasBroadbandService && <button type="button" role="switch" aria-checked={!!bbActive} className={`cm-sw${bbActive ? ' on' : ''}`} disabled={bbBusy} onClick={toggleBb}><span className="cm-sw-track" aria-hidden="true"><span /></span>Broadband {bbActive ? 'Active' : 'Inactive'}</button>}
              </div>
            </div>
          </div>
        </header>

        <Tabs tabs={tabs} value={tab} onChange={go} />

        {tab === 'overview' && (
          <div role="tabpanel" id="cm-panel-overview" aria-labelledby="cm-tab-overview">
            {hasBB && (<>
              <div className="cm-block">{bbPlanDl}</div>
              <h2 className="cm-h cm-sec">Live traffic</h2>
              {customer.broadband?.username
                ? <div className="cm-flat">{nodes.bbLive}</div>
                : <div className="cm-block cm-pad">No PPPoE username on this customer, so there is no live traffic to read.</div>}
              <Disclosure icon={Cpu} title="Current hardware" sub={[customer.broadband?.routerModel, customer.broadband?.ponNumber ? `PON ${customer.broadband.ponNumber}` : null, customer.broadband?.username].filter(Boolean).join(' · ')}>
                {nodes.bbHw}
                {customer.broadband?.username && <Row icon={Router} title="ONU details" sub="Open this customer in the ONU list" onClick={openOnu} />}
              </Disclosure>
              <div className="cm-flat">{nodes.bbHist}</div>
              <div className="cm-flat">{nodes.bbUptime}</div>
            </>)}
            {catvSolo && catvDetails}
            {!hasBB && !catvSolo && (
            <div className={`cm-next ${next.tone}`} role="status">
              <div className="cm-next-title">{NextIcon ? <NextIcon size={22} aria-hidden="true" /> : <CheckCircle2 size={22} aria-hidden="true" />}<span>{next.title}</span></div>
              <div className="cm-next-sub">{next.sub}</div>
              {next.cta && (
                <button type="button" className="cm-btn primary block" onClick={() => (next.screen ? setScreen(next.screen) : go(next.tab))}>{next.cta}</button>
              )}
            </div>
            )}

            <h2 className="cm-sr">Services</h2>
            {(bothServices || (linked || []).length > 0) && <div className="cm-block">
              {(linked || []).length > 0 && <h2 className="cm-h">Linked accounts</h2>}
              {bothServices && hasCatvService && (
                <Row icon={Tv} title={<>Cable TV <Chip tone={catvOn ? 'ok' : 'bad'}>{catvOn ? 'Active' : 'Inactive'}</Chip></>}
                  sub={<>{catvPlan || 'No package'}<br />{catvOn ? `${fmtDate(customer.catv?.expiryDate)} · ${catvV.label}` : (customer.catv?.closureReason ? `Closed: ${customer.catv.closureReason}` : 'Connection closed')}{customer.catv?.gtplSuspended ? ' · Suspended on GTPL' : ''}</>}
                  onClick={() => openService('cabletv')} />
              )}
              {bothServices && hasBroadbandService && (
                <Row icon={Wifi} title={<>Broadband <Chip tone={bbActive ? 'ok' : 'bad'}>{bbActive ? 'Active' : 'Inactive'}</Chip></>}
                  sub={<>{bbPlan || 'No plan'}<br />{bbActive ? `${fmtDate(customer.broadband?.expiryDate)} · ${bbV.label}` : (customer.broadband?.closureReason ? `Closed: ${customer.broadband.closureReason}` : 'Connection closed')}</>}
                  onClick={() => openService('broadband')} />
              )}
              {(linked || []).map(l => (
                <Row key={l.id} icon={Link2} title={`${l.label}: ${l.name}`} sub={l.expiry ? `${fmtDate(l.expiry)} · ${validity(l.expiry).label}` : 'Linked account'} onClick={() => goToCustomer(l.id)} />
              ))}
            </div>}

            {(pendingText || notes) && (
              <div className="cm-block">
                {pendingText && <Row icon={Clock} title="Collection pending" sub={pendingText} />}
                {notes && (
                  <div className="cm-pad">
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 800, marginBottom: 4 }}><StickyNote size={16} aria-hidden="true" />Notes</div>
                    <div className={`cm-note${notesOpen ? '' : ' cm-clamp'}`}>{notes}</div>
                    {notes.length > 120 && <button type="button" className="cm-btn" style={{ marginTop: 8 }} aria-expanded={notesOpen} onClick={() => setNotesOpen(o => !o)}>{notesOpen ? 'Show less' : 'Show full note'}</button>}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === 'services' && (
          <div role="tabpanel" id="cm-panel-services" aria-labelledby="cm-tab-services">
            {bothServices && (
              <div className="cm-seg" role="radiogroup" aria-label="Choose service">
                <button type="button" role="radio" aria-checked={svc === 'cabletv'} onClick={() => setSvc('cabletv')}><Tv size={18} aria-hidden="true" />Cable TV</button>
                <button type="button" role="radio" aria-checked={svc === 'broadband'} onClick={() => setSvc('broadband')}><Wifi size={18} aria-hidden="true" />Broadband</button>
              </div>
            )}
            {(!bothServices ? (hasCatvService ? 'cabletv' : 'broadband') : svc) === 'cabletv' ? catvBlock : bbBlock}
          </div>
        )}

        {tab === 'payments' && (
          <div role="tabpanel" id="cm-panel-payments" aria-labelledby="cm-tab-payments">
            <div className="cm-block">
              <div className="cm-pad">
                <div className="cm-svc-top">
                  <h2 className="cm-svc-name">Outstanding</h2>
                  {balance > 0 ? <Chip tone="bad">Dues pending</Chip> : <Chip tone="ok">No dues</Chip>}
                </div>
                <div style={{ fontSize: '1.75rem', fontWeight: 800, marginTop: 4 }}>₹{fmt(balance)}</div>
                {lastPay && <div style={{ fontSize: '.9375rem', color: 'var(--cm-muted)' }}>Last payment ₹{fmt(lastPay.amount)} on {fmtDate(lastPay.paymentDate)}</div>}
              </div>
            </div>
            <h2 className="cm-h" style={{ padding: '0 2px 8px' }}>Payment history</h2>
            <div className="cm-block cm-pad">{panelFor('balance_history')}</div>
          </div>
        )}

        {tab === 'complaints' && (
          <div role="tabpanel" id="cm-panel-complaints" aria-labelledby="cm-tab-complaints">
            <div className="cm-block cm-pad">{panelFor('complaints')}</div>
          </div>
        )}

        {tab === 'more' && (
          <div role="tabpanel" id="cm-panel-more" aria-labelledby="cm-tab-more">
            <div className="cm-block">
              {can('edit') && <Row icon={User} title="Customer details" sub="Edit name, mobile, address, notes, status" onClick={() => setScreen('edit')} />}
              {can('history') && <Row icon={History} title="Activity history" sub="Every change made to this customer" onClick={() => setScreen('history')} />}
              {can('documents') && <Row icon={FileText} title="Documents" sub="ID proof, photos, KYC files" onClick={() => setScreen('documents')} />}
              {can('app_login') && <Row icon={KeyRound} title="App login" sub="Customer's login for the portal and app" onClick={() => setScreen('app_login')} />}
              {can('link_account') && <Row icon={Link2} title="Link account" sub="Join a Cable TV and Broadband account" onClick={() => setScreen('link_account')} />}
            </div>
            {hasCatvService && can('gtpl_saathi') && (
              <div className="cm-block">
                <h2 className="cm-h">GTPL</h2>
                <Row icon={ShoppingCart} title="GTPL Saathi" sub="Orders, renewals, tickets, live status" onClick={() => setScreen('gtpl_saathi')} />
              </div>
            )}
            {isAdmin && (
              <div className="cm-block">
                <h2 className="cm-h">Danger zone</h2>
                <Row danger icon={Trash2} title="Delete customer" sub="Permanent. Asks you to confirm." onClick={onDelete} />
              </div>
            )}
          </div>
        )}
        {tab === 'overview' && ((!hideCollect && can('collect')) || can('renew')) && (
          <div className="cm-dock">
            {!hideCollect && can('collect') && <button type="button" className="cm-btn primary" onClick={() => setScreen('collect')}><Wallet size={18} aria-hidden="true" />Collect</button>}
            {can('renew') && <button type="button" className="cm-btn primary" onClick={() => setScreen('renew')}><RefreshCw size={18} aria-hidden="true" />Renew</button>}
          </div>
        )}
      </div>

      {infoOpen && (
        <CustomerInfoOverlay customer={customer} hasCatv={hasCatvService} hasBroadband={hasBroadbandService}
          onClose={() => setInfoOpen(false)}
          onEdit={can('edit') ? () => { setInfoOpen(false); setScreen('edit'); } : null} />
      )}
      {screen && <Screen title={labels[screen] || 'Details'} onClose={closeScreen}>{panelFor(screen)}</Screen>}
    </>
  );
}
